import type { Ingreso, Egreso, Inversion } from '../types';
import { formatCurrency } from '../utils/currency-utils';

export interface ResumenMensual {
  id: string;
  mes: number;
  año: number;
  fechaGeneracion: Date;
  
  // Ingresos
  totalIngresos: number;
  ingresosPorCategoria: { [categoria: string]: number };
  ingresosMasAltos: Ingreso[];
  
  // Egresos
  totalEgresos: number;
  egresosPorCategoria: { [categoria: string]: number };
  egresosMasAltos: Egreso[];
  
  // Balance
  balance: number;
  porcentajeAhorro: number;
  
  // Inversiones
  totalInversiones: number;
  inversionesPorTipo: { [tipo: string]: number };
  rendimientoMensual: number;
  
  // Patrimonio
  patrimonioNeto: number;
  variacionPatrimonio: number;
  
  // Métricas adicionales
  gastoPromedioDiario: number;
  ingresoPromedioDiario: number;
  diasConGastos: number;
  diasConIngresos: number;
  
  // Comparación con mes anterior
  comparacionMesAnterior: {
    ingresos: { valor: number; porcentaje: number };
    egresos: { valor: number; porcentaje: number };
    balance: { valor: number; porcentaje: number };
    patrimonio: { valor: number; porcentaje: number };
  };
  
  // Insights y recomendaciones
  insights: string[];
  recomendaciones: string[];
}

export class ResumenService {
  
  generateResumenMensual(
    año: number,
    mes: number,
    ingresos: Ingreso[],
    egresos: Egreso[],
    inversiones: Inversion[],
    resumenAnterior?: ResumenMensual
  ): ResumenMensual {
    
    // Filtrar datos del mes específico
    const ingresosMes = this.filterByMonth(ingresos, año, mes);
    const egresosMes = this.filterByMonth(egresos, año, mes);
    const inversionesMes = this.filterByMonth(inversiones, año, mes);
    
    // Calcular totales
    const totalIngresos = this.calculateTotal(ingresosMes);
    const totalEgresos = this.calculateTotal(egresosMes);
    const totalInversiones = this.calculateTotal(inversionesMes);
    const balance = totalIngresos - totalEgresos;
    
    // Calcular agrupaciones por categoría
    const ingresosPorCategoria = this.groupByCategory(ingresosMes);
    const egresosPorCategoria = this.groupByCategory(egresosMes);
    const inversionesPorTipo = this.groupByType(inversionesMes);
    
    // Calcular métricas adicionales
    const diasEnMes = new Date(año, mes + 1, 0).getDate();
    const gastoPromedioDiario = totalEgresos / diasEnMes;
    const ingresoPromedioDiario = totalIngresos / diasEnMes;
    const porcentajeAhorro = totalIngresos > 0 ? (balance / totalIngresos) * 100 : 0;
    
    // Calcular patrimonio neto
    const patrimonioNeto = totalIngresos - totalEgresos + totalInversiones;
    
    // Obtener elementos más significativos
    const ingresosMasAltos = ingresosMes
      .sort((a, b) => b.monto - a.monto)
      .slice(0, 3);
    const egresosMasAltos = egresosMes
      .sort((a, b) => b.monto - a.monto)
      .slice(0, 3);
    
    // Calcular comparación con mes anterior
    const comparacionMesAnterior = this.calculateComparison(
      { totalIngresos, totalEgresos, balance, patrimonio: patrimonioNeto },
      resumenAnterior
    );
    
    // Generar insights y recomendaciones
    const insights = this.generateInsights(
      balance, porcentajeAhorro, egresosPorCategoria, comparacionMesAnterior
    );
    
    const recomendaciones = this.generateRecomendations(
      balance, porcentajeAhorro, egresosPorCategoria, comparacionMesAnterior
    );
    
    return {
      id: `resumen-${año}-${mes}`,
      mes,
      año,
      fechaGeneracion: new Date(),
      totalIngresos,
      ingresosPorCategoria,
      ingresosMasAltos,
      totalEgresos,
      egresosPorCategoria,
      egresosMasAltos,
      balance,
      porcentajeAhorro,
      totalInversiones,
      inversionesPorTipo,
      rendimientoMensual: this.calculateRendimiento(inversionesMes),
      patrimonioNeto,
      variacionPatrimonio: resumenAnterior ? patrimonioNeto - resumenAnterior.patrimonioNeto : 0,
      gastoPromedioDiario,
      ingresoPromedioDiario,
      diasConGastos: this.countDaysWithTransactions(egresosMes),
      diasConIngresos: this.countDaysWithTransactions(ingresosMes),
      comparacionMesAnterior,
      insights,
      recomendaciones
    };
  }
  
  private filterByMonth<T extends { fecha: Date }>(items: T[], año: number, mes: number): T[] {
    return items.filter(item => {
      const fecha = item.fecha;
      return fecha.getFullYear() === año && fecha.getMonth() === mes;
    });
  }
  
  private calculateTotal<T extends { monto: number }>(items: T[]): number {
    return items.reduce((sum, item) => sum + item.monto, 0);
  }
  
  private groupByCategory<T extends { categoria: string; monto: number }>(
    items: T[]
  ): { [categoria: string]: number } {
    const grouped: { [categoria: string]: number } = {};
    items.forEach(item => {
      grouped[item.categoria] = (grouped[item.categoria] || 0) + item.monto;
    });
    return grouped;
  }
  
  private groupByType<T extends { tipo: string; monto: number }>(
    items: T[]
  ): { [tipo: string]: number } {
    const grouped: { [tipo: string]: number } = {};
    items.forEach(item => {
      grouped[item.tipo] = (grouped[item.tipo] || 0) + item.monto;
    });
    return grouped;
  }
  
  private calculateRendimiento(inversiones: Inversion[]): number {
    return inversiones.reduce((sum, inv) => {
      // Calcular rendimiento basado en el valor actual vs monto inicial
      const rendimiento = (inv.monto * 0.05); // Rendimiento estimado del 5% mensual
      return sum + rendimiento;
    }, 0);
  }
  
  private countDaysWithTransactions<T extends { fecha: Date }>(items: T[]): number {
    const uniqueDays = new Set(
      items.map(item => item.fecha.toDateString())
    );
    return uniqueDays.size;
  }
  
  private calculateComparison(
    current: { totalIngresos: number; totalEgresos: number; balance: number; patrimonio: number },
    anterior?: ResumenMensual
  ) {
    if (!anterior) {
      return {
        ingresos: { valor: 0, porcentaje: 0 },
        egresos: { valor: 0, porcentaje: 0 },
        balance: { valor: 0, porcentaje: 0 },
        patrimonio: { valor: 0, porcentaje: 0 }
      };
    }
    
    const calculateChange = (current: number, previous: number) => {
      const valor = current - previous;
      const porcentaje = previous !== 0 ? (valor / Math.abs(previous)) * 100 : 0;
      return { valor, porcentaje };
    };
    
    return {
      ingresos: calculateChange(current.totalIngresos, anterior.totalIngresos),
      egresos: calculateChange(current.totalEgresos, anterior.totalEgresos),
      balance: calculateChange(current.balance, anterior.balance),
      patrimonio: calculateChange(current.patrimonio, anterior.patrimonioNeto)
    };
  }
  
  private generateInsights(
    balance: number,
    porcentajeAhorro: number,
    egresosPorCategoria: { [categoria: string]: number },
    comparacion: ResumenMensual['comparacionMesAnterior']
  ): string[] {
    const insights: string[] = [];
    
    // Insight sobre balance
    if (balance > 0) {
      insights.push(`Mes positivo con un ahorro de ${formatCurrency(balance)}`);
    } else {
      insights.push(`Mes deficitario con ${formatCurrency(Math.abs(balance))} de diferencia negativa`);
    }
    
    // Insight sobre porcentaje de ahorro
    if (porcentajeAhorro >= 20) {
      insights.push(`Excelente capacidad de ahorro del ${porcentajeAhorro.toFixed(1)}%`);
    } else if (porcentajeAhorro >= 10) {
      insights.push(`Buena capacidad de ahorro del ${porcentajeAhorro.toFixed(1)}%`);
    } else if (porcentajeAhorro > 0) {
      insights.push(`Capacidad de ahorro limitada del ${porcentajeAhorro.toFixed(1)}%`);
    }
    
    // Insight sobre categoría de mayor gasto
    const mayorGasto = Object.entries(egresosPorCategoria)
      .sort(([,a], [,b]) => b - a)[0];
    if (mayorGasto) {
      insights.push(`Mayor gasto en ${mayorGasto[0]}: ${formatCurrency(mayorGasto[1])}`);
    }
    
    // Insight sobre comparación con mes anterior
    if (comparacion.balance.porcentaje > 10) {
      insights.push(`Mejora significativa del ${comparacion.balance.porcentaje.toFixed(1)}% respecto al mes anterior`);
    } else if (comparacion.balance.porcentaje < -10) {
      insights.push(`Deterioro del ${Math.abs(comparacion.balance.porcentaje).toFixed(1)}% respecto al mes anterior`);
    }
    
    return insights;
  }
  
  private generateRecomendations(
    balance: number,
    porcentajeAhorro: number,
    egresosPorCategoria: { [categoria: string]: number },
    comparacion: ResumenMensual['comparacionMesAnterior']
  ): string[] {
    const recomendaciones: string[] = [];
    
    // Recomendaciones basadas en balance
    if (balance < 0) {
      recomendaciones.push('Considera reducir gastos no esenciales para equilibrar tu presupuesto');
    }
    
    // Recomendaciones basadas en ahorro
    if (porcentajeAhorro < 10) {
      recomendaciones.push('Intenta ahorrar al menos el 10% de tus ingresos mensuales');
    }
    
    // Recomendaciones sobre gastos por categoría
    const categoriasMayorGasto = Object.entries(egresosPorCategoria)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 2);
    
    categoriasMayorGasto.forEach(([categoria, monto]) => {
      if (monto > 0) {
        recomendaciones.push(`Revisa tus gastos en ${categoria} que representan ${formatCurrency(monto)}`);
      }
    });
    
    // Recomendaciones basadas en tendencias
    if (comparacion.egresos.porcentaje > 15) {
      recomendaciones.push('Tus gastos aumentaron significativamente, considera establecer límites por categoría');
    }
    
    if (balance > 0 && porcentajeAhorro > 15) {
      recomendaciones.push('Considera invertir parte de tus ahorros para generar rendimientos adicionales');
    }
    
    return recomendaciones;
  }
  
  getResumenesHistory(
    ingresos: Ingreso[],
    egresos: Egreso[],
    inversiones: Inversion[],
    mesesAtras: number = 12
  ): ResumenMensual[] {
    const resumenes: ResumenMensual[] = [];
    const currentDate = new Date();
    
    for (let i = mesesAtras - 1; i >= 0; i--) {
      const fecha = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const año = fecha.getFullYear();
      const mes = fecha.getMonth();
      
      const resumenAnterior = resumenes[resumenes.length - 1];
      const resumen = this.generateResumenMensual(
        año, mes, ingresos, egresos, inversiones, resumenAnterior
      );
      
      resumenes.push(resumen);
    }
    
    return resumenes;
  }
  
  getCurrentMonthResumen(
    ingresos: Ingreso[],
    egresos: Egreso[],
    inversiones: Inversion[]
  ): ResumenMensual {
    const currentDate = new Date();
    const año = currentDate.getFullYear();
    const mes = currentDate.getMonth();
    
    return this.generateResumenMensual(año, mes, ingresos, egresos, inversiones);
  }
}